export const STORAGE_BUCKETS = {
  PUBLIC_PROJECT_THUMBNAILS: 'public-project-thumbnails',
  COMPANY_DOCUMENTS: (companyId: string) => `company-documents-${companyId}`,
  COMPANY_IMAGES: (companyId: string) => `company-images-${companyId}`,
} as const;

export const STORAGE_PATHS = {
  // Public paths
  projectThumbnail: (companyId: string, projectId: string, filename: string) => 
    `${companyId}/${projectId}/project-images/${filename}`,
  
  // Company document paths
  projectDocument: (projectId: string, filename: string) => 
    `projects/${projectId}/all-documents/${filename}`,
  
  // Company image paths
  questionReferenceImage: (
    projectId: string, 
    stageId: string, 
    questionUuid: string, 
    filename: string
  ) => 
    `projects/${projectId}/production-stages/${stageId}/quality-checklist-questions/${questionUuid}/reference-images/${filename}`,
  
  qualityCheckAnswerImage: (
    projectId: string,
    qualityCheckId: string,
    questionUuid: string,
    filename: string
  ) =>
    `projects/${projectId}/quality-checks/${qualityCheckId}/answer-images/${questionUuid}/${filename}`,
  
  taskAttachment: (taskId: string, filename: string) =>
    `tasks/${taskId}/attachments/${filename}`,

  followUpCommunicationAttachment: (
    threadId: string,
    communicationId: string,
    filename: string
  ) =>
    `follow-ups/threads/${threadId}/communications/${communicationId}/attachments/${filename}`,

  emailAttachment: (emailId: string, filename: string) =>
    `emails/${emailId}/attachments/${filename}`,
} as const;

// File size limits
export const STORAGE_LIMITS = {
  PUBLIC_PROJECT_THUMBNAILS: 20 * 1024 * 1024, // 20MB
  COMPANY_DOCUMENTS: 50 * 1024 * 1024, // 50MB
  COMPANY_IMAGES: 40 * 1024 * 1024, // 40MB
} as const;

// Allowed MIME types
export const ALLOWED_MIME_TYPES = {
  PUBLIC_PROJECT_THUMBNAILS: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'],
  COMPANY_DOCUMENTS: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/plain',
    'image/jpeg',
    'image/png'
  ],
  COMPANY_IMAGES: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'],
} as const;

// Signed URL expiration times (in seconds)
export const SIGNED_URL_EXPIRATION = {
  DEFAULT: 4 * 60 * 60, // 4 hours
  DOCUMENT: 4 * 60 * 60, // 4 hours
  IMAGE: 4 * 60 * 60, // 4 hours
  ATTACHMENT: 4 * 60 * 60, // 4 hours
  THUMBNAIL: 24 * 60 * 60, // 24 hours (thumbnails accessed more frequently)
} as const;

// Helper function to get expiration time for a URL type
export function getSignedUrlExpiration(urlType?: 'document' | 'image' | 'attachment' | 'thumbnail'): number {
  return urlType && SIGNED_URL_EXPIRATION[urlType.toUpperCase() as keyof typeof SIGNED_URL_EXPIRATION] 
    ? SIGNED_URL_EXPIRATION[urlType.toUpperCase() as keyof typeof SIGNED_URL_EXPIRATION]
    : SIGNED_URL_EXPIRATION.DEFAULT;
}